import './index.css';
import './i18n';
import React, { lazy, Suspense } from 'react';
import App from './App';
import { createRoot } from 'react-dom/client';
import reportWebVitals from './reportWebVitals';
import { AuthProvider } from "react-oidc-context";
import { WebStorageStateStore } from 'oidc-client-ts';
import { TenantProvider } from './context/TenantContext'
import { BrandingProvider } from './context/BrandingContext'
import { BrowserRouter as Router } from 'react-router-dom';

import * as Sentry from "@sentry/react";

// Handle ChunkLoadError - happens when cached HTML references old chunks after deployment
function handleChunkError() {
  const lastReload = sessionStorage.getItem('chunkErrorReload');
  const now = Date.now();
  if (!lastReload || now - parseInt(lastReload) > 10000) {
    sessionStorage.setItem('chunkErrorReload', now.toString());
    window.location.reload();
  }
}

window.addEventListener('error', (event) => {
  // JS chunk errors come with a message
  if (event.message && event.message.includes('ChunkLoadError')) {
    handleChunkError();
    return;
  }
  // CSS chunk errors fire as resource errors on <link> elements without a message
  if (event.target && event.target.tagName === 'LINK' && event.target.href && event.target.href.includes('/static/css/')) {
    handleChunkError();
  }
}, true); // useCapture=true to catch resource errors

// Also catch unhandled promise rejections for dynamic imports
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason && event.reason.name === 'ChunkLoadError') {
    handleChunkError();
  }
});

Sentry.init({
  dsn: "https://df67896016fd2582077aca5a56581b7a@o4510561318928384.ingest.de.sentry.io/4510561320632400",
  enabled: !window.location.hostname.includes('localhost'),
  sendDefaultPii: true,
  integrations: [
    Sentry.replayIntegration()
  ],
  beforeSend(event) {
    // Don't report ChunkLoadErrors — these are expected after deployments
    const message = event.exception?.values?.[0]?.value || '';
    if (message.includes('ChunkLoadError') || message.includes('Loading chunk') || message.includes('Loading CSS chunk')) {
      return null;
    }
    return event;
  },
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const cognitoAuthConfig = {
  authority: process.env.REACT_APP_OIDC_AUTHORITY,
  client_id: process.env.REACT_APP_OIDC_CLIENT_ID,
  redirect_uri: process.env.REACT_APP_LOGIN_REDIRECT_URL,
  response_type: 'code',
  scope: process.env.REACT_APP_OIDC_SCOPE || 'openid profile email',
  automaticSilentRenew: true,
  onSigninCallback: () => {},
  // Use localStorage instead of sessionStorage to share auth across browser tabs
  userStore: new WebStorageStateStore({ store: window.localStorage }),
};

// Plan 103, REQ-15: the PUBLIC certificate page is gated ABOVE AuthProvider so it
// is fully no-auth (no oidc network activity, no tenant/branding providers). It
// matches the certificates hostname in prod/dev, or the /c/ path locally so
// Playwright can cover it without DNS.
const PublicCertificateApp = lazy(() => import('./pages/certificate/PublicCertificateApp'));

function isPublicCertificateRoute() {
  const host = window.location.hostname || '';
  const path = window.location.pathname || '';
  return (
    host.startsWith('certificates.') ||
    host.startsWith('dev-certificates.') ||
    path.startsWith('/c/')
  );
}

const root = createRoot(document.getElementById('root'));

if (isPublicCertificateRoute()) {
  root.render(
    <React.StrictMode>
      <Suspense fallback={<div style={{ minHeight: '100vh', background: '#0b1220' }} />}>
        <PublicCertificateApp />
      </Suspense>
    </React.StrictMode>
  );
} else {
  root.render(
    <React.StrictMode>
      <Router>
        <AuthProvider {...cognitoAuthConfig}>
          <TenantProvider>
            <BrandingProvider>
              <App />
            </BrandingProvider>
          </TenantProvider>
        </AuthProvider>
      </Router>
    </React.StrictMode>
  );
}

reportWebVitals();